import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  AlertCircle,
  AlertTriangle,
  Briefcase,
  FileCheck2,
  GraduationCap,
  MoreHorizontal,
  Stethoscope,
  Trash2,
  Upload,
} from 'lucide-react'
import {
  type ChangeEvent,
  type FormEvent,
  type ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  type Absence,
  type AbsenceHalf,
  type AbsenceType,
  api,
  type VacationBlackout,
} from '../lib/api'
import { invalidateAbsenceData } from '../lib/invalidate'
import { notify } from '../lib/notify'
import { Button } from './ui/button'
import { confirm } from './ui/confirm-dialog'
import { Input } from './ui/input'
import { Label } from './ui/label'
import { Select } from './ui/select'
import { SidePanel } from './ui/side-panel'

export const TYPE_LABEL: Record<AbsenceType, string> = {
  vacation: 'Urlaub',
  sick: 'Krank',
  training: 'Fortbildung',
  other: 'Sonstiges',
  overtime_reduction: 'Überstundenabbau',
  maternity: 'Mutterschutz',
  parental: 'Elternzeit',
}

export const TYPE_ICON: Record<AbsenceType, typeof Briefcase> = {
  vacation: Briefcase,
  sick: Stethoscope,
  training: GraduationCap,
  other: MoreHorizontal,
  overtime_reduction: MoreHorizontal,
  maternity: MoreHorizontal,
  parental: MoreHorizontal,
}

export const TYPE_COLOR: Record<AbsenceType, string> = {
  vacation: '#3B82F6',
  sick: '#EF4444',
  training: '#10B981',
  other: '#A3978C',
  overtime_reduction: '#A3978C',
  maternity: '#A3978C',
  parental: '#A3978C',
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10)
}

function diffDaysInclusive(fromIso: string, toIso: string): number {
  const f = new Date(fromIso)
  const t = new Date(toIso)
  const ms = 86_400_000
  return Math.round((t.getTime() - f.getTime()) / ms) + 1
}

/**
 * Werktage zwischen zwei ISO-Daten. Optionale `firstDayHalf` / `lastDayHalf`
 * lassen den ersten bzw. letzten Tag nur als 0.5 zählen. Bei Single-Day
 * (from === to) zählt nur `firstDayHalf`.
 */
export function workdaysBetween(
  fromIso: string,
  toIso: string,
  firstDayHalf?: AbsenceHalf | null,
  lastDayHalf?: AbsenceHalf | null,
): number {
  const from = new Date(fromIso)
  const to = new Date(toIso)
  const sameDay = fromIso.slice(0, 10) === toIso.slice(0, 10)
  let count = 0
  const cur = new Date(from)
  while (cur <= to) {
    const day = cur.getDay()
    if (day !== 0 && day !== 6) {
      let value = 1
      const isFirst = cur.getTime() === from.getTime()
      const isLast = cur.getTime() === to.getTime()
      if (sameDay) {
        if (firstDayHalf) value = 0.5
      } else {
        if (isFirst && firstDayHalf) value = 0.5
        else if (isLast && lastDayHalf) value = 0.5
      }
      count += value
    }
    cur.setDate(cur.getDate() + 1)
  }
  return count
}

export function formatAbsenceDate(iso: string): string {
  return new Date(iso).toLocaleDateString('de-DE', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  })
}

/** Returns blackouts whose range overlaps [from, to]. Compares ISO date strings
 * directly — both sides are YYYY-MM-DD so lexicographic order matches calendar
 * order. The component is also responsible for narrowing to applicable teams,
 * but at the form layer we already only show what the API returns. */
function overlappingBlackouts(
  blackouts: VacationBlackout[],
  fromIso: string,
  toIso: string,
): VacationBlackout[] {
  const f = fromIso.slice(0, 10)
  const t = toIso.slice(0, 10)
  return blackouts.filter((b) => {
    const bf = b.fromDate.slice(0, 10)
    const bt = b.toDate.slice(0, 10)
    return !(bt < f || bf > t)
  })
}

interface AbsenceFormDialogProps {
  open: boolean
  absence: Absence | null
  onClose: () => void
}

export interface AbsenceFormBodyResult {
  title: string
  description?: string
  body: ReactNode
  footer: ReactNode
  isDirty: boolean
}

interface UseAbsenceFormArgs {
  open: boolean
  absence: Absence | null
  onClose: () => void
  defaultDate?: string
}

export function useAbsenceForm({
  open,
  absence,
  onClose,
  defaultDate,
}: UseAbsenceFormArgs): AbsenceFormBodyResult {
  const queryClient = useQueryClient()
  const isEdit = !!absence
  const initialDate = defaultDate ? defaultDate.slice(0, 10) : todayIso()
  const [type, setType] = useState<AbsenceType>('vacation')
  const [fromDate, setFromDate] = useState(initialDate)
  const [toDate, setToDate] = useState(initialDate)
  const [notes, setNotes] = useState('')
  const [firstDayHalf, setFirstDayHalf] = useState<AbsenceHalf | ''>('')
  const [lastDayHalf, setLastDayHalf] = useState<AbsenceHalf | ''>('')
  const [substituteUserId, setSubstituteUserId] = useState<string>('')
  const [overlapError, setOverlapError] = useState<string | null>(null)
  const [dirty, setDirty] = useState(false)
  const fileInputRef = useRef<HTMLInputElement | null>(null)

  // Colleagues for the Vertretung dropdown — current user's team members
  const { data: teamData } = useQuery({
    queryKey: ['team-members'],
    queryFn: () => api.listTeamMembers(),
    enabled: open,
  })
  const { data: meData } = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: () => api.me(),
    enabled: open,
  })
  const colleagues = useMemo(() => {
    const all = teamData?.users ?? []
    return all.filter((u) => u.id !== (meData?.user?.id ?? ''))
  }, [teamData, meData])

  // Active blackouts + vacation rules — load once when the dialog is opened.
  const { data: blackoutsData } = useQuery({
    queryKey: ['vacation-blackouts'],
    queryFn: () => api.listVacationBlackouts(),
    enabled: open,
  })
  const { data: rulesData } = useQuery({
    queryKey: ['vacation-rules'],
    queryFn: () => api.getVacationRules(),
    enabled: open,
  })

  useEffect(() => {
    if (!open) return
    if (absence) {
      setType(absence.type)
      setFromDate(absence.fromDate.slice(0, 10))
      setToDate(absence.toDate.slice(0, 10))
      setNotes(absence.notes ?? '')
      setFirstDayHalf(absence.firstDayHalf ?? '')
      setLastDayHalf(absence.lastDayHalf ?? '')
      setSubstituteUserId(absence.substituteUserId ?? '')
    } else {
      const d = defaultDate ? defaultDate.slice(0, 10) : todayIso()
      setType('vacation')
      setFromDate(d)
      setToDate(d)
      setNotes('')
      setFirstDayHalf('')
      setLastDayHalf('')
      setSubstituteUserId('')
    }
    setOverlapError(null)
    setDirty(false)
  }, [open, absence, defaultDate])

  const markDirty = () => setDirty(true)
  const sameDay = fromDate === toDate

  // Derived warnings displayed inline in the form
  const blackoutHits = useMemo(() => {
    if (type !== 'vacation') return [] as VacationBlackout[]
    return overlappingBlackouts(blackoutsData?.blackouts ?? [], fromDate, toDate)
  }, [type, blackoutsData, fromDate, toDate])

  const ruleHint = useMemo(() => {
    if (type !== 'vacation' || !rulesData?.rules) return null
    const { vacationMinLeadDays, vacationMaxConsecutiveDays } = rulesData.rules
    const messages: string[] = []
    if (vacationMinLeadDays > 0) {
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      const from = new Date(fromDate)
      const days = Math.floor((from.getTime() - today.getTime()) / 86_400_000)
      if (days < vacationMinLeadDays) {
        messages.push(
          `Urlaub muss mindestens ${vacationMinLeadDays} Tage im Voraus beantragt werden (aktuell ${Math.max(0, days)} Tage).`,
        )
      }
    }
    if (vacationMaxConsecutiveDays > 0) {
      const span = diffDaysInclusive(fromDate, toDate)
      if (span > vacationMaxConsecutiveDays) {
        messages.push(
          `Maximaler Urlaubsantrag: ${vacationMaxConsecutiveDays} Tage am Stück (aktuell ${span}).`,
        )
      }
    }
    return messages.length > 0 ? messages : null
  }, [type, rulesData, fromDate, toDate])

  const sickNoteRequired = type === 'sick' && diffDaysInclusive(fromDate, toDate) >= 3

  const save = useMutation({
    mutationFn: () => {
      const data = {
        type,
        fromDate,
        toDate,
        notes: notes || null,
        firstDayHalf: firstDayHalf || null,
        // Single-day absences don't use lastDayHalf
        lastDayHalf: sameDay ? null : lastDayHalf || null,
        substituteUserId: substituteUserId || null,
      }
      if (isEdit && absence) return api.updateAbsence(absence.id, data)
      return api.createAbsence(data)
    },
    onSuccess: (res) => {
      invalidateAbsenceData(queryClient)
      notify.success(isEdit ? 'Abwesenheit aktualisiert' : 'Abwesenheit erfasst')
      if (res?.warning) notify.warning(res.warning)
      onClose()
    },
    onError: (err: Error) => {
      // Overlap check returns 409 — surface inline as well as toast.
      // Sperrzeit-block returns 409 with "Sperrzeit:..." — same treatment.
      const msg = err.message
      if (msg.includes('Überschneidung') || msg.startsWith('Sperrzeit:')) {
        setOverlapError(msg)
      }
      notify.error(msg)
    },
  })

  const remove = useMutation({
    mutationFn: () => api.deleteAbsence(absence?.id ?? ''),
    onSuccess: () => {
      invalidateAbsenceData(queryClient)
      notify.success('Abwesenheit gelöscht')
      onClose()
    },
    onError: (err: Error) => notify.error(err.message),
  })

  const uploadSickNote = useMutation({
    mutationFn: (file: File) => api.uploadAbsenceSickNote(absence?.id ?? '', file),
    onSuccess: () => {
      invalidateAbsenceData(queryClient)
      notify.success('AU-Bescheinigung hochgeladen')
    },
    onError: (err: Error) => notify.error(err.message),
  })

  const onPickFile = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (file.type !== 'application/pdf') {
      notify.error('Nur PDF erlaubt')
      return
    }
    uploadSickNote.mutate(file)
    e.target.value = ''
  }

  const onDelete = async () => {
    if (!absence) return
    const ok = await confirm({
      title: 'Abwesenheit löschen?',
      description: `${TYPE_LABEL[absence.type]} ${formatAbsenceDate(absence.fromDate)} – ${formatAbsenceDate(absence.toDate)}`,
      variant: 'danger',
      confirmLabel: 'Löschen',
    })
    if (ok) remove.mutate()
  }

  const onSubmit = (e: FormEvent) => {
    e.preventDefault()
    if (new Date(toDate) < new Date(fromDate)) {
      notify.error('Bis-Datum muss nach Von-Datum liegen')
      return
    }
    setOverlapError(null)
    save.mutate()
  }

  const isPending = save.isPending || remove.isPending

  const body = (
    <form onSubmit={onSubmit} className="space-y-4 pb-2">
      <div className="space-y-2">
        <Label htmlFor="abs-type">Typ</Label>
        <Select
          id="abs-type"
          value={type}
          onChange={(e) => {
            setType(e.target.value as AbsenceType)
            markDirty()
          }}
        >
          <option value="vacation">Urlaub</option>
          <option value="sick">Krank</option>
          <option value="training">Fortbildung</option>
          <option value="other">Sonstiges</option>
        </Select>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-2">
          <Label htmlFor="abs-from">Von</Label>
          <Input
            id="abs-from"
            type="date"
            value={fromDate}
            onChange={(e) => {
              setFromDate(e.target.value)
              markDirty()
            }}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="abs-to">Bis</Label>
          <Input
            id="abs-to"
            type="date"
            value={toDate}
            onChange={(e) => {
              setToDate(e.target.value)
              markDirty()
            }}
          />
        </div>
      </div>
      {/* Half-day selector — segmented look so it visually reads as part
          of the date range above. Single-day shows one row; multi-day
          shows two compact rows side-by-side. */}
      {sameDay ? (
        <div className="space-y-1.5">
          <Label>Tageslänge</Label>
          <HalfDaySegment
            value={firstDayHalf}
            onChange={(v) => {
              setFirstDayHalf(v)
              markDirty()
            }}
            ariaLabel="Halber Tag wählen"
          />
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label>Erster Tag</Label>
            <HalfDaySegment
              value={firstDayHalf}
              onChange={(v) => {
                setFirstDayHalf(v)
                markDirty()
              }}
              ariaLabel="Erster Tag — halb oder ganz"
            />
          </div>
          <div className="space-y-1.5">
            <Label>Letzter Tag</Label>
            <HalfDaySegment
              value={lastDayHalf}
              onChange={(v) => {
                setLastDayHalf(v)
                markDirty()
              }}
              ariaLabel="Letzter Tag — halb oder ganz"
            />
          </div>
        </div>
      )}
      {/* Vertretung */}
      <div className="space-y-2">
        <Label htmlFor="abs-substitute">Vertretung (optional)</Label>
        <Select
          id="abs-substitute"
          value={substituteUserId}
          onChange={(e) => {
            setSubstituteUserId(e.target.value)
            markDirty()
          }}
        >
          <option value="">Keine Vertretung</option>
          {colleagues.map((u) => (
            <option key={u.id} value={u.id}>
              {u.name}
            </option>
          ))}
        </Select>
        <p className="text-2xs text-text-tertiary">
          Kolleg:in, die während deiner Abwesenheit übernimmt. Wird bei der Genehmigung automatisch
          per Push benachrichtigt.
        </p>
      </div>
      <div className="space-y-2">
        <Label htmlFor="abs-notes">Notiz (optional)</Label>
        <Input
          id="abs-notes"
          value={notes}
          onChange={(e) => {
            setNotes(e.target.value)
            markDirty()
          }}
          placeholder="z.B. Familienurlaub Toskana"
        />
      </div>

      {/* Inline warnings */}
      {ruleHint && (
        <div className="rounded-lg border border-warning/40 bg-warning/10 px-3 py-2 text-xs text-warning space-y-1">
          <div className="flex items-center gap-1.5 font-medium">
            <AlertTriangle className="h-3.5 w-3.5" /> Urlaubsregeln
          </div>
          {ruleHint.map((m) => (
            <p key={m} className="leading-snug">
              {m}
            </p>
          ))}
        </div>
      )}
      {blackoutHits.length > 0 &&
        (() => {
          const isBlock = blackoutHits.some((b) => b.blockMode === 'block')
          const Icon = isBlock ? AlertTriangle : AlertCircle
          return (
            <div
              className={`rounded-lg border px-3 py-2 text-xs space-y-1 ${
                isBlock
                  ? 'border-danger/40 bg-danger/10 text-danger'
                  : 'border-warning/40 bg-warning/10 text-warning'
              }`}
            >
              <div className="flex items-center gap-1.5 font-medium">
                <Icon className="h-3.5 w-3.5" />
                {isBlock ? 'Gesperrter Zeitraum' : 'Hinweis Sperrzeit'}
              </div>
              {blackoutHits.map((b) => (
                <p key={b.id} className="leading-snug">
                  {b.name} ({formatAbsenceDate(b.fromDate)} – {formatAbsenceDate(b.toDate)})
                  {b.blockMode === 'block' ? ' — Urlaub nicht möglich' : ''}
                </p>
              ))}
            </div>
          )
        })()}

      {/* Sick note section — only relevant when type=sick */}
      {type === 'sick' && (
        <div className="space-y-2 rounded-lg border border-border-subtle bg-bg-overlay/40 px-3 py-2.5">
          <div className="flex items-center gap-2 text-xs">
            {sickNoteRequired || absence?.sickNoteRequired ? (
              <span className="inline-flex items-center gap-1.5 rounded-md bg-danger/10 border-2 border-danger/60 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider text-danger">
                <AlertTriangle className="h-3.5 w-3.5" /> AU-Bescheinigung erforderlich
              </span>
            ) : (
              <span className="text-text-tertiary">
                Ab 3 Krankheitstagen am Stück wird eine AU-Bescheinigung benötigt.
              </span>
            )}
          </div>
          {isEdit && absence?.sickNoteDocumentId ? (
            <div className="flex items-center gap-2 text-xs">
              <FileCheck2 className="h-3.5 w-3.5 text-success" />
              <a
                href={api.documentFileUrl(absence.sickNoteDocumentId)}
                target="_blank"
                rel="noreferrer"
                className="text-accent underline-offset-2 hover:underline"
              >
                AU-Bescheinigung anzeigen
              </a>
            </div>
          ) : isEdit && absence ? (
            <div>
              <input
                ref={fileInputRef}
                type="file"
                accept="application/pdf"
                onChange={onPickFile}
                className="hidden"
              />
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadSickNote.isPending}
              >
                <Upload className="h-3.5 w-3.5" />
                {uploadSickNote.isPending ? 'Lädt hoch…' : 'AU-Bescheinigung hochladen (PDF)'}
              </Button>
            </div>
          ) : (
            <p className="text-2xs text-text-tertiary">
              Nach Anlegen der Krankmeldung kann die AU-Bescheinigung hier hochgeladen werden.
            </p>
          )}
        </div>
      )}

      {overlapError && (
        <div className="rounded-lg border border-danger/40 bg-danger/10 px-3 py-2 text-xs text-danger">
          {overlapError}
        </div>
      )}
    </form>
  )

  const footer = (
    <>
      {isEdit && (
        <Button
          size="sm"
          variant="ghost"
          onClick={onDelete}
          type="button"
          disabled={isPending}
          className="mr-auto text-danger hover:bg-danger/10 hover:text-danger"
        >
          <Trash2 className="h-3.5 w-3.5" /> Löschen
        </Button>
      )}
      <Button variant="ghost" onClick={onClose} type="button">
        Abbrechen
      </Button>
      <Button onClick={onSubmit} type="button" disabled={isPending}>
        {isEdit ? 'Speichern' : 'Anlegen'}
      </Button>
    </>
  )

  return {
    title: isEdit ? 'Abwesenheit bearbeiten' : 'Neue Abwesenheit',
    // Beschreibungszeile wie in den anderen beiden Erfassen-Tabs (C5)
    description: isEdit
      ? 'Zeitraum, Tageslänge und Vertretung anpassen.'
      : 'Urlaub, Krankheit oder Fortbildung beantragen.',
    body,
    footer,
    isDirty: dirty,
  }
}

/** Render-prop wrapper for `useAbsenceForm`. */
export function AbsenceFormBody(props: {
  open: boolean
  absence: Absence | null
  onClose: () => void
  defaultDate?: string
  children: (result: AbsenceFormBodyResult) => ReactNode
}) {
  const { children, ...rest } = props
  const result = useAbsenceForm(rest)
  return <>{children(result)}</>
}

export function AbsenceFormDialog({ open, absence, onClose }: AbsenceFormDialogProps) {
  const { title, body, footer } = useAbsenceForm({ open, absence, onClose })
  return (
    <SidePanel open={open} onClose={onClose} title={title} footer={footer}>
      {body}
    </SidePanel>
  )
}

/** Compact segmented control for half-day selection. Three states:
 *   "" (Ganzer Tag) · "morning" (vormittags ½) · "afternoon" (nachmittags ½).
 * Replaces the chunky <Select> dropdown so the choice feels like part of the
 * date row rather than a separate form section. */
function HalfDaySegment({
  value,
  onChange,
  ariaLabel,
}: {
  value: AbsenceHalf | ''
  onChange: (next: AbsenceHalf | '') => void
  ariaLabel: string
}) {
  const opts: { id: AbsenceHalf | ''; label: string }[] = [
    { id: '', label: 'Ganz' },
    { id: 'morning', label: '½ Vorm.' },
    { id: 'afternoon', label: '½ Nachm.' },
  ]
  return (
    <fieldset
      className="inline-flex w-full rounded-lg border border-border-default bg-bg-overlay/40 p-0.5"
      aria-label={ariaLabel}
    >
      {opts.map((o) => {
        const active = value === o.id
        return (
          <button
            key={o.id}
            type="button"
            aria-pressed={active}
            onClick={() => onChange(o.id)}
            className={`flex-1 rounded-md px-2 py-1.5 text-xs transition-colors ${
              active
                ? 'bg-accent/15 text-accent font-medium'
                : 'text-text-secondary hover:text-text-primary'
            }`}
          >
            {o.label}
          </button>
        )
      })}
    </fieldset>
  )
}
